import axios from 'axios';
import { FormEvent, useEffect, useState } from 'react';
import { GoogleLogin } from 'react-google-login';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { RouteComponentProps, useLocation, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import google from '../../assets/icons/backOffice/google.svg';
import { Button } from '../../components/common/Button/Button';
import { InputBackOffice } from '../../components/common/InputBackoffice';
import Spinner from '../../components/common/Spinner';
import { ErrorToast } from '../../components/common/Toasts/Toasts';
import { validateEmail } from '../../helpers/helperFunctions';
import { removeUserFromLS } from '../../helpers/localstorage-helper';
import {
  DASHBOARD,
  LOGIN,
  LOGIN_TRAVELUTION,
  REFERRAL
} from '../../routes/routes';
import {
  continueToAffiliateTerms,
  logoutRemoveState,
  signIn,
  signInGoogle
} from '../../slices/auth-slice';

import { CaptionText } from '../../styles/FontStyles';
import { AuthLayoutTitle } from '../../styles/GlobalComponents';
import { colors } from '../../styles/GlobalStyles';

import { SocialButton } from './SocialButton';

const googleAppId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const captchaId = process.env.REACT_APP_CAPTCHA_ID;

type TParams = { id?: string; referralCode?: string };

const LoginTravelution = ({ history, match }: RouteComponentProps<TParams>) => {
  const { t } = useTranslation('onboarding');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  let captchaResult = '';

  const [referrerId, setReferrerId] = useState(Number(match.params.id) ?? null);
  const [isWithReferral, setIsWithReferral] = useState(true);

  const [captchaLoading, setCaptchaLoading] = useState(false);

  const dispatch = useAppDispatch();
  let { referralCode }: any = useParams();
  referralCode = referralCode ?? null;

  const { loading } = useAppSelector((state) => state.auth);

  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  const finup = searchParams.get('finup');
  const finupTest = searchParams.get('finupTest');

  const simplicity = searchParams.get('simplicity');

  const simplicityRedirectUri = searchParams.get('redirect_uri');

  const onHandleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email) {
      ErrorToast(t('login.error_enter_id_or_email'));
    } else if (!validateEmail(email)) {
      ErrorToast(t('login.error_email_invalid'));
      return;
    } else if (!password) {
      ErrorToast(t('login.error_enter_password'));
    } else {
      setCaptchaLoading(true);
      window?.initGeetest4(
        {
          captchaId,
          language: 'eng',
          product: 'bind',
          riskType: 'slide',
          nativeButton: {
            width: '100%',
            marginBottom: '20px'
          }
        },
        function (captcha: any) {
          captcha.showBox();
          captcha
            .onReady(function () {
              setCaptchaLoading(false);
            })
            .onSuccess(function () {
              const result = captcha.getValidate();
              dispatch(
                signIn({
                  email,
                  password,
                  result,
                  history,
                  finup,
                  finupTest,
                  simplicity,
                  simplicityRedirectUri
                })
              );
            })
            .onError(function () {
              setCaptchaLoading(false);
            });
        }
      );
    }
  };

  const responseGoogle = (response: any) => {
    if (response && response?.tokenId) {
      const { tokenId: token } = response;
      dispatch(
        signInGoogle({
          token,
          referrerId,
          captchaResult,
          history,
          finup,
          finupTest,
          simplicity,
          simplicityRedirectUri
        })
      );
    }
  };

  useEffect(() => {
    if (
      location.pathname === DASHBOARD ||
      location.pathname === LOGIN_TRAVELUTION
    ) {
      setIsWithReferral(false);
    } else {
      setIsWithReferral(true);
    }

    setReferrerIdByCodeIfAfiliateRegistration();

    removeUserFromLS();

    dispatch(logoutRemoveState());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, location]);

  const setReferrerIdByCodeIfAfiliateRegistration = async () => {
    if (referralCode !== null) {
      const { data } = await axios.get(`/user/referral-code/${referralCode}`);

      setReferrerId(data.data.user.id);
    }
  };

  return (
    <div>
      {loading && <Spinner fullScreen={true} transparent />}
      {captchaLoading && <Spinner fullScreen={true} transparent />}

      {!isWithReferral && (
        <AuthLayoutTitle>{t('login.sign_in_title')}</AuthLayoutTitle>
      )}

      {!isWithReferral && (
        <form onSubmit={onHandleSubmit}>
          <InputBackOffice
            id={'id'}
            type={'text'}
            placeholder={t('login.email_placeholder')}
            name={'email'}
            value={email.replace(/\s+/g, '')}
            onChange={(e: any) => setEmail(e.replace(/\s+/g, ''))}
            label={t('login.email_label')}
            style={{ textTransform: 'lowercase' }}
          />
          <InputBackOffice
            id={'password'}
            type={'password'}
            placeholder={t('login.password_placeholder')}
            name={'password'}
            value={password}
            onChange={setPassword}
            label={t('login.password_label')}
          />

          <CaptionText
            right
            pointer
            semiBold
            color={colors.primary900}
            onClick={() => history.push('/forgotten-password')}>
            {t('login.forgot_you_password')}
          </CaptionText>
          <Button
            m='20px 0 10px 0'
            type='submit'
            disabled={!email && !password}>
            {t('login.sign_in')}
          </Button>
        </form>
      )}

      {!isWithReferral && (
        <CaptionText
          pointer
          semiBold
          center
          mb='30'
          color={colors.primary900}
          onClick={() => history.push(REFERRAL)}>
          {t('login.create_account')}
        </CaptionText>
      )}

      {isWithReferral && match.params.id && (
        <Button
          onClick={() => {
            dispatch(
              continueToAffiliateTerms({
                referrerId: parseInt(match.params.id || ''),
                history
              })
            );
          }}>
          {t('login.create_account')}
        </Button>
      )}

      <GoogleLogin
        clientId={googleAppId || ''}
        onSuccess={responseGoogle}
        onFailure={responseGoogle}
        cookiePolicy={'single_host_origin'}
        render={(renderProps) => (
          <SocialButton
            text={t('login.continue_with_google')}
            icon={google}
            onHandleLogin={() => {
              window.initGeetest4(
                {
                  captchaId,
                  language: 'eng',
                  product: 'bind',
                  riskType: 'slide',
                  nativeButton: {
                    width: '100%',
                    marginBottom: '20px'
                  }
                },
                function (captcha: any) {
                  captcha.showBox();
                  captcha
                    .onReady(function () {})
                    .onSuccess(function () {
                      const result = captcha.getValidate();

                      captchaResult = result;

                      renderProps.onClick();
                    })
                    .onError(function () {});
                }
              );
            }}
          />
        )}
      />

      {isWithReferral && (
        <CaptionText center m='16px 0 0 0'>
          {t('login.already_have_account')}{' '}
          <span
            style={{
              color: colors.primary900,
              cursor: 'pointer',
              fontWeight: 500
            }}
            onClick={() => history.push('/login')}>
            {t('login.sign_in')}
          </span>
        </CaptionText>
      )}
    </div>
  );
};

export default LoginTravelution;
